// import { RenderCellTypes } from "types/RenderCellTypes";
// import { metricTypeEnum } from "app/pages/SalesDemoPage/data/type";

import { metricTypeEnum } from "app/pages/SalesPortalPage/LayoutV1/data/type";
import { RenderCellTypes } from "./RenderCellTypes";
import { GridSortModel } from "@mui/x-data-grid";

// enums for commonly used string values
export enum PageMode {
  Scroll = "scroll",
  // add more page modes if needed
}

export enum RenderMode {
  Server = "server",
  Client = "client",
}

// existing types
type ApiDetailsType = {
  endpoint: string;
  pathParams?: any;
  queryParams?: any;
};

type RenderCellMapperType = {
  field: string;
  renderCell: string;
  /**
   * Optional extra props forwarded into the rendered cell component (spread last).
   * Use this to configure reusable cells per-grid without forking them
   * (e.g. {@link CellMetadataModal} `headerChipFields`).
   */
  cellProps?: Record<string, unknown>;
};

/** Optional MUI column sizing merged after dynamic headers (per-field). */
export type DataGridColumnDefOverride = {
  flex?: number;
  minWidth?: number;
  width?: number;
  maxWidth?: number;
};

// options interface for DataGridTypes
interface DataGridOptions {
  apiDetails: ApiDetailsType;
  pageSize?: number;
  pageMode?: PageMode;
  filterable?: boolean;
  renderMode?: RenderMode;
  editable?: boolean;
  exportable?: boolean;
  gridHeight?: number;
  autoHeightBuffer?: number;
  exportFileName?: string;
  columnResizable?: boolean;
  isRowSelectable?: boolean | ((params: any) => boolean);
  isOpportunity?: boolean;
  multiSelectable?: boolean;
  renderCellMapping?: RenderCellMapperType[];
  visibleColumns?: string[];
  columnSortModel?: GridSortModel;
  filterableFields?: string[];
  /** Optional initial filter model (e.g. for offers: is_active, end_date). Sent in filters query param. */
  initialFilterModel?: { items: Array<{ id?: number; columnField: string; operatorValue: string; value?: any }> };
  /** Applied in DataGrid provider after header generation (e.g. flex for wide text columns). */
  columnDefOverrides?: Record<string, DataGridColumnDefOverride>;
}

class DataGridTypes {
  apiDetails: ApiDetailsType;
  pageSize: number;
  pageMode: PageMode;
  filterable: boolean;
  renderMode: RenderMode;
  editable: boolean;
  exportable: boolean;
  gridHeight?: number;
  autoHeightBuffer: number;
  exportFileName: string;
  columnResizable: boolean;
  isRowSelectable: boolean | ((params: any) => boolean);
  isOpportunity: boolean;
  multiSelectable?: boolean;
  renderCellMapping?: RenderCellMapperType[];
  visibleColumns?: string[];
  columnSortModel?: GridSortModel;
  filterableFields?: string[];
  initialFilterModel?: { items: Array<{ id?: number; columnField: string; operatorValue: string; value?: any }> };
  columnDefOverrides?: Record<string, DataGridColumnDefOverride>;

  // the constructor now accepts a single options object
  constructor(options: DataGridOptions) {
    // assign with defaults
    this.apiDetails = options.apiDetails;
    this.pageSize = options.pageSize ?? 0;
    this.pageMode = options.pageMode ?? PageMode.Scroll;
    this.filterable = options.filterable ?? false;
    this.renderMode = options.renderMode ?? RenderMode.Server;
    this.editable = options.editable ?? false;
    this.exportable = options.exportable ?? false;
    this.gridHeight = options.gridHeight;
    this.autoHeightBuffer = options.autoHeightBuffer ?? 30;
    this.exportFileName = options.exportFileName ?? "-";
    this.columnResizable = options.columnResizable ?? true;
    this.isRowSelectable = options.isRowSelectable ?? false;
    this.isOpportunity = options.isOpportunity ?? false;
    this.multiSelectable = options.multiSelectable;
    this.renderCellMapping = options.renderCellMapping;
    this.visibleColumns = options.visibleColumns || [];
    this.columnSortModel = options.columnSortModel || [];
    this.filterableFields = options.filterableFields || [];
    this.initialFilterModel = options.initialFilterModel;
    this.columnDefOverrides = options.columnDefOverrides;
  }

  // static API version constants
  static API_VERSION_1: string = 'api/v1';
  static API_VERSION_2: string = 'api/v2';

  // static methods returning pre-configured grid types remain usable as before
  static isAvailableRowSelectable = (params: any) => {
    const status = `${
      params?.row?.nurturing_status || params?.row?.nurturingStatus || ''
    }`.toUpperCase();
    return status === 'AVAILABLE';
  };

  static UPLOAD_DATA_DETAILS = (uploadId: string, selectedDealer: any, uploadSummary: any) =>
    new DataGridTypes({
      apiDetails: {
        endpoint: `${DataGridTypes.API_VERSION_2}/data-feed/details`,
        pathParams: { id: uploadId },
        queryParams: { dealer: selectedDealer.dealer },
      },
      pageSize: 30,
      pageMode: PageMode.Scroll,
      filterable: true,
      renderMode: RenderMode.Server,
      editable: true,
      exportable: true,
      gridHeight: 700,
      exportFileName: selectedDealer?.site_title?.split(" ").join("_"),
      columnResizable: true,
      isRowSelectable: true,
      isOpportunity: true,
      multiSelectable: false,
      renderCellMapping: [
        {
          field: 'name',
          renderCell: RenderCellTypes.BUTTON,
        },
        {
          field: 'lead_created_date_crm',
          renderCell: RenderCellTypes.DATE,
        },
      ],
    });

  static USERS_DATA = (quickFilters?: Record<string, string[]>) => {
    const now = new Date();
    const formattedDate = `${now.getFullYear()}${(now.getMonth() + 1)
      .toString().padStart(2, '0')}${now.getDate().toString().padStart(2, '0')}_${now.getHours()
        .toString().padStart(2, '0')}${now.getMinutes().toString().padStart(2, '0')}${now.getSeconds()
          .toString().padStart(2, '0')}`;

    return new DataGridTypes({
      apiDetails: {
        endpoint: `${DataGridTypes.API_VERSION_2}/users/datagrid?audit_meta.created_user_type=USER`,
        queryParams:
          quickFilters && Object.keys(quickFilters).length > 0
            ? Object.fromEntries(
                Object.entries(quickFilters)
                  .filter(([, values]) => Array.isArray(values) && values.length > 0)
                  .map(([key, values]) => [key, values.join(',')]),
              )
            : undefined,
      },
      pageSize: 0,
      pageMode: PageMode.Scroll,
      filterable: true,
      renderMode: RenderMode.Client,
      editable: false,
      exportable: true,
      // gridHeight: 500,
      autoHeightBuffer: 40,
      exportFileName: `users_export_${formattedDate}`,
      columnResizable: true,
      isRowSelectable: true,
      isOpportunity: true,
      multiSelectable: false,
      renderCellMapping: [
        {
          field: 'name',
          renderCell: RenderCellTypes.BUTTON,
        },
        {
          field: 'status',
          renderCell: RenderCellTypes.STATUS,
        },
        {
          field: 'created_at',
          renderCell: RenderCellTypes.DATE,
        },
      ],
      visibleColumns: ["name", "email", "roles", "dealer_group_id", "created_at", "status"],
      filterableFields: ["name", "email", "roles", "dealer_group_id", "status"],
    });
  }

  static CAMPAIGNS = (parentGroup: string, dealerId: string) => {
    return new DataGridTypes({
      apiDetails: {
        endpoint: `${DataGridTypes.API_VERSION_2}/campaigns`,
        queryParams: { dealer: dealerId, parent_group: parentGroup },
      },
      pageSize: 20,
      pageMode: PageMode.Scroll,
      filterable: true,
      renderMode: RenderMode.Server,
      editable: false,
      exportable: true,
      gridHeight: 700,
      exportFileName: "campaigns_export",
      columnResizable: true,
      isRowSelectable: true,
      isOpportunity: true,
      multiSelectable: true,
      renderCellMapping: [
        { field: 'name', renderCell: RenderCellTypes.BUTTON_WITH_ACTION_ICONS },
        { field: 'no_of_leads', renderCell: RenderCellTypes.COUNT_WITH_ICON },
        { field: 'leads_in_nurturing', renderCell: RenderCellTypes.COUNT_WITH_ICON },
        { field: 'status', renderCell: RenderCellTypes.STATUS },
        { field: 'type', renderCell: RenderCellTypes.TEXT },
        { field: 'last_email_sent_date', renderCell: RenderCellTypes.DATE },
        { field: 'last_added_date', renderCell: RenderCellTypes.DATE },
      ],
      columnSortModel: [
        { field: 'name', sort: 'asc' },
      ],
      visibleColumns: ['name', 'no_of_leads', 'leads_in_nurturing', 'status', 'type', 'last_email_sent_date', 'last_added_date'],
      filterableFields: ['name', 'no_of_leads', 'leads_in_nurturing', 'status', 'type', 'last_email_sent_date', 'last_added_date'],
    });
  }
  static DEALERS_DATA = () =>
    new DataGridTypes({
      apiDetails: { endpoint: `${DataGridTypes.API_VERSION_2}/dealers/datagrid` },
      pageSize: 20,
      pageMode: PageMode.Scroll,
      filterable: true,
      renderMode: RenderMode.Server,
      editable: false,
      exportable: true,
      // gridHeight: 660,
      autoHeightBuffer: 40,
      exportFileName: "all_dealership_grid",
      columnResizable: true,
      isRowSelectable: true,
      isOpportunity: true,
      multiSelectable: false,
      renderCellMapping: [
        { field: 'name', renderCell: RenderCellTypes.BUTTON },
        { field: 'status', renderCell: RenderCellTypes.STATUS },
        { field: 'segments_no', renderCell: RenderCellTypes.POPOVER_INFO },
        { field: 'prospects', renderCell: RenderCellTypes.POPOVER_INFO },
        { field: 'prospects_in_nurturing', renderCell: RenderCellTypes.POPOVER_INFO },
      ],
      columnSortModel: [
        { field: 'name', sort: 'asc' },
      ],
      visibleColumns: ['name', 'dealer_group', 'manufacturers', 'segments_no', 'prospects', 'prospects_in_nurturing', 'status'],
      filterableFields: ['name', 'dealer_group', 'manufacturers', 'segments_no', 'prospects', 'prospects_in_nurturing', 'status'],
    });
  static DEALERS_MANAGEMENT = () =>
    new DataGridTypes({
      apiDetails: { endpoint: `${DataGridTypes.API_VERSION_2}/dealers/datagrid` },
      pageSize: 20,
      pageMode: PageMode.Scroll,
      filterable: true,
      renderMode: RenderMode.Server,
      editable: false,
      exportable: true,
      // gridHeight: 700,
      autoHeightBuffer: 40,
      exportFileName: "all_dealership_grid",
      columnResizable: true,
      isRowSelectable: true,
      isOpportunity: true,
      multiSelectable: false,
      renderCellMapping: [
        // { field: 'name', renderCell: RenderCellTypes.BUTTON },
        { field: 'status', renderCell: RenderCellTypes.STATUS },
        { field: 'segments_no', renderCell: RenderCellTypes.POPOVER_INFO },
        { field: 'prospects', renderCell: RenderCellTypes.POPOVER_INFO },
        { field: 'prospects_in_nurturing', renderCell: RenderCellTypes.POPOVER_INFO },
      ],
      columnSortModel: [
        { field: 'name', sort: 'asc' },
      ],
      visibleColumns: ['name', 'dealer_group', 'manufacturers', 'segments_no', 'prospects', 'prospects_in_nurturing', 'status'],
      filterableFields: ['name', 'dealer_group', 'manufacturers', 'segments_no', 'prospects', 'prospects_in_nurturing', 'status'],
    });

  static DEALERS_GROUP_DATA = () =>
    new DataGridTypes({
      apiDetails: { endpoint: `${DataGridTypes.API_VERSION_2}/dealer-groups/datagrid?audit_meta.created_user_type=USER` },
      pageSize: 20,
      pageMode: PageMode.Scroll,
      filterable: true,
      renderMode: RenderMode.Server,
      editable: false,
      exportable: true,
      // gridHeight: 500,
      autoHeightBuffer: 40,
      exportFileName: "dealer_groups_export",
      columnResizable: true,
      isRowSelectable: true,
      isOpportunity: true,
      multiSelectable: false,
      renderCellMapping: [
        { field: 'name', renderCell: RenderCellTypes.BUTTON },
      ],
      visibleColumns: ["name", "makes", "manufacturer", "type"],
      filterableFields: ["name", "makes", "manufacturer", "type"],
    });

  // static ROLES_DATA = () =>
  //   new DataGridTypes({
  //     apiDetails: { endpoint: `${DataGridTypes.API_VERSION_2}/roles` },
  //     pageSize: 0,
  //     pageMode: PageMode.Scroll,
  //     filterable: true,
  //     renderMode: RenderMode.Server,
  //     editable: false,
  //     exportable: true,
  //     gridHeight: 500,
  //     exportFileName: "roles-export",
  //     columnResizable: true,
  //     isRowSelectable: true,
  //     isOpportunity: false,
  //     multiSelectable: false,
  //     renderCellMapping: [
  //       { field: 'name', renderCell: RenderCellTypes.BUTTON },
  //       { field: 'status', renderCell: RenderCellTypes.STATUS }
  //     ],
  //     visibleColumns: ["name", "description", "permissions", "status", "created_at", "updated_at"],
  //     columnSortModel: [{ field: 'name', sort: 'asc' }]
  //   });

  static LEADS = (
    dealerId: string,
    group_id: string,
    opts?: {
      leadDetailsAsButton?: boolean;
      multiSelectable?: boolean;
      timeperiod?: string | number;
      start_date?: string;
      end_date?: string;
    },
  ) =>
    new DataGridTypes({
      apiDetails: {
        endpoint: `${DataGridTypes.API_VERSION_2}/leads`,
        queryParams: {
          dealer: dealerId,
          group_entity_filter: JSON.stringify({
            group_id,
          }),
          ...(opts?.timeperiod === 'CUSTOM_RANGE' && opts?.start_date && opts?.end_date
            ? {
                timeperiod: opts.timeperiod,
                startDate: opts.start_date,
                endDate: opts.end_date,
              }
            : opts?.timeperiod
            ? {
                timeperiod: opts.timeperiod,
              }
            : {}),
        },
      },
      pageSize: 20,
      pageMode: PageMode.Scroll,
      filterable: true,
      renderMode: RenderMode.Server,
      editable: false,
      exportable: true,
      // gridHeight: 800,
      autoHeightBuffer: 30,
      exportFileName: `${dealerId}`,
      columnResizable: true,
      isRowSelectable: opts?.multiSelectable
        ? DataGridTypes.isAvailableRowSelectable
        : true,
      isOpportunity: true,
      multiSelectable: opts?.multiSelectable ?? true,
      renderCellMapping: [
        { field: 'name', renderCell: RenderCellTypes.BUTTON },
        {
          field: 'lead_details_in_a_string',
          renderCell: opts?.leadDetailsAsButton
            ? RenderCellTypes.BUTTON_WITH_COPY
            : RenderCellTypes.TEXT_WITH_COPY,
        },
        { field: 'latest_lead_active_date', renderCell: RenderCellTypes.DATE },
        { field: 'nurturing_status', renderCell: RenderCellTypes.STATUS },
      ],
      visibleColumns: ["lead_details_in_a_string", "segment", "engagement_index", "total_messages_sent", "latest_lead_active_date", "nurturing_status", "dealership_location", "middle_name", "address", "city", "state", "zip", "phone", "phone1", "phone2", "unsubscribes", "lead_status", "lead_type", "source", "sales_person", "voi_year", "voi_make", "voi_model", "voi_stock", "voi_vin", "voi_status", "voi_trim", "voi_price", "first_lead_creation_date", "unique_crm_lead_id", "unique_crm_customer_id"],
      filterableFields: [
        "lead_details_in_a_string",
        "phone",
        "nurturing_status",
        "voi_year",
        "voi_make",
        "voi_model",
        "voi_stock",
        "voi_vin",
        "voi_status",
        "source"
      ],
    });
  static SEGMENTS = (
    dealerId: string,
    timeperiod?: string | number,
    start_date?: string,
    end_date?: string,
  ) =>
    new DataGridTypes({
      apiDetails: {
        endpoint: `${DataGridTypes.API_VERSION_2}/segments`,
        queryParams: {
          dealer: dealerId,
          ...(timeperiod === 'CUSTOM_RANGE' && start_date && end_date
            ? {
                timeperiod,
                startDate: start_date,
                endDate: end_date,
              }
            : timeperiod
            ? { timeperiod }
            : {}),
        },
      },
      pageSize: 20,
      pageMode: PageMode.Scroll,
      filterable: false,
      renderMode: RenderMode.Server,
      editable: false,
      exportable: true,
      // gridHeight: 800,
      exportFileName: "segments-export",
      columnResizable: true,
      isRowSelectable: true,
      isOpportunity: true,
      multiSelectable: true,
      renderCellMapping: [
        { field: 'name', renderCell: RenderCellTypes.BUTTON_WITH_ICON },
        { field: 'no_of_leads', renderCell: RenderCellTypes.COUNT_WITH_ICON },
        { field: 'LEAD_CONTACTED', renderCell: RenderCellTypes.NUMBER },
        { field: 'status', renderCell: RenderCellTypes.STATUS },
        { field: 'last_added_date', renderCell: RenderCellTypes.DATE },
        { field: 'created_at', renderCell: RenderCellTypes.DATE },
        { field: 'updated_at', renderCell: RenderCellTypes.DATE },
      ],
      visibleColumns: ["name", "no_of_leads", "LEAD_CONTACTED", "status", "last_added_date", "leads_in_nurturing", "created_at", "updated_at"],
      filterableFields: ["name", "no_of_leads", "LEAD_CONTACTED", "status", "last_added_date", "leads_in_nurturing", "created_at", "updated_at"],
    });

  static SALES_DEMO = (renderData: any, mySqlFlag: Boolean, filterType: string, timeperiod: string | number, start_date: string, end_date: string) => {
    let endpoint = `${DataGridTypes.API_VERSION_2}`;
    if (
      filterType === metricTypeEnum.opportunityAddedInNurturing ||
      filterType === metricTypeEnum.leadContacted ||
      filterType === metricTypeEnum.leadSold
    ) {
      endpoint += '/leads';
    } else {
      endpoint += '/segments';
    }

    let queryParams = {};
    if (timeperiod === 'CUSTOM_RANGE') {
      queryParams = {
        group_entity_filter: JSON.stringify(renderData.data?.group_entity_filter) || {},
        dealer: renderData.dealershipId,
        mysql: mySqlFlag,
        lead_filter: JSON.stringify(renderData.data?.lead_filter),
        startDate: start_date,
        endDate: end_date,
      };
    } else {
      queryParams = {
        group_entity_filter: JSON.stringify(renderData.data?.group_entity_filter) || {},
        dealer: renderData.dealershipId,
        mysql: mySqlFlag,
        lead_filter: JSON.stringify(renderData.data?.lead_filter),
        timeperiod: timeperiod,
      };
    }
    const now = new Date();
    const formattedDate = `${now.getFullYear()}${(now.getMonth() + 1)
      .toString().padStart(2, '0')}${now.getDate().toString().padStart(2, '0')}_${now.getHours()
        .toString().padStart(2, '0')}${now.getMinutes().toString().padStart(2, '0')}${now.getSeconds()
          .toString().padStart(2, '0')}`;
    const dealerName = (renderData.dealershipName || 'dealer').replace(/\s+/g, '_').replace(/[^a-zA-Z0-9_-]/g, '');

    return new DataGridTypes({
      apiDetails: { endpoint, queryParams },
      pageSize: 20,
      pageMode: PageMode.Scroll,
      filterable: true,
      renderMode: RenderMode.Server,
      editable: false,
      exportable: true,
      gridHeight: 700,
      autoHeightBuffer: 30,
      exportFileName: `${dealerName}_${filterType}_${timeperiod}_${formattedDate}`.toLowerCase(),
      columnResizable: true,
      isRowSelectable: true,
      isOpportunity: true,
      multiSelectable: false,
      renderCellMapping: [
        { field: 'name', renderCell: RenderCellTypes.BUTTON },
        { field: 'first_name', renderCell: RenderCellTypes.BUTTON },
        { field: 'no_of_leads', renderCell: RenderCellTypes.COUNT_WITH_ICON },
        { field: 'LEAD_CONTACTED', renderCell: RenderCellTypes.COUNT_WITH_ICON },
        { field: 'LEAD_SOLD', renderCell: RenderCellTypes.COUNT_WITH_ICON },
        { field: 'lead_created_date_crm', renderCell: RenderCellTypes.DATE },
        { field: 'first_lead_creation_date', renderCell: RenderCellTypes.DATE },
        { field: 'latest_lead_active_date', renderCell: RenderCellTypes.DATE },
        { field: 'enrolled_date', renderCell: RenderCellTypes.DATE },
        { field: 'latest_contacted_date', renderCell: RenderCellTypes.DATE },
        { field: 'date_sold', renderCell: RenderCellTypes.DATE },
        { field: 'nurturing_status', renderCell: RenderCellTypes.STATUS },
        { field: 'conversion_rate_raw', renderCell: RenderCellTypes.RATE },
      ],
      filterableFields: [
        'name',
        'Altoona, PA',
        'Email',
        'Address1',
        'City',
        'State',
        'Zip',
        'Cell Phone',
        'Home Phone',
        'Work Phone',
        'DoNotEmail',
        'Status',
        'lead_type',
        'Source Description',
        'SalesRepName',
        'Wanted Veh Year',
        'Wanted Veh Make',
        'Wanted Veh Model',
        'Vehicle 1 Stock Number',
        'Vehicle 1 Vin',
        'New/Used',
        'Wanted Veh Trim',
        'Wanted Veh Price',
        'Date In',
        'Date Ir',
        'unique_crm_lead_id',
        'unique_crm_customer_id',
        'segment',
        'engagement_index',
        'total_messages_sent',
        'first_name',
        'Email_mailshake',
        'Enrolled Date',
        'Date Contacted',
        'Lead-to',
        'Sold Y/N',
        'Days in Nurturin',
        '# Nurturing M',
        'Enrolled Status',
        'Enrolled Source',
        'Enrolled VOI',
        'Sold Source',
        'Vehicle Bought',
        'Sales Person',
        'status',
        'no_of_leads',
        'LEAD_CONTACTED',
        'LEAD_SOLD',
        'conversion_rate_raw',
        'lead_created_date_crm',
        "enrolled_date",
        "date_sold",
        'lead_to_sale_days',
        'days_in_nurturing',
        'number_of_nurturing_messages',
        'vehicle_bought',
        'sold_vin',
        'sold_source',
        'sales_person',
        'enrolled_lead_status',
        'enrolled_source',
        'enrolled_voi',
        'latest_contacted_date',
        'latest_lead_active_date',
        'lead_to_contacted_days',
        'dealership_location',
        'sold_yn',
        'email',
        'first_lead_creation_date',
        'address',
      ],
    });
  }
  static OPPORTUNITIES = (renderData: any, mySqlFlag: Boolean, filterType: string, timeperiod: string | number, start_date: string, end_date: string) => {
    let endpoint = `${DataGridTypes.API_VERSION_2}`;
    if (
      filterType === metricTypeEnum.opportunityAddedInNurturing ||
      filterType === metricTypeEnum.leadContacted ||
      filterType === metricTypeEnum.leadSold
    ) {
      endpoint += '/leads';
    } else {
      endpoint += '/segments';
    }

    let queryParams = {};
    if (timeperiod === 'CUSTOM_RANGE') {
      queryParams = {
        group_entity_filter: JSON.stringify(renderData.data?.group_entity_filter) || {},
        dealer: renderData.dealershipId,
        lead_filter: JSON.stringify(renderData.data?.lead_filter),
        startDate: start_date,
        endDate: end_date,
      };
    } else {
      queryParams = {
        group_entity_filter: JSON.stringify(renderData.data?.group_entity_filter) || {},
        dealer: renderData.dealershipId,
        lead_filter: JSON.stringify(renderData.data?.lead_filter),
        timeperiod: timeperiod,
      };
    }
    const now = new Date();
    const formattedDate = `${now.getFullYear()}${(now.getMonth() + 1)
      .toString().padStart(2, '0')}${now.getDate().toString().padStart(2, '0')}_${now.getHours()
        .toString().padStart(2, '0')}${now.getMinutes().toString().padStart(2, '0')}${now.getSeconds()
          .toString().padStart(2, '0')}`;
    const dealerName = (renderData.dealershipName || 'dealer').replace(/\s+/g, '_').replace(/[^a-zA-Z0-9_-]/g, '');

    return new DataGridTypes({
      apiDetails: { endpoint, queryParams },
      pageSize: 20,
      pageMode: PageMode.Scroll,
      filterable: true,
      renderMode: RenderMode.Server,
      editable: false,
      exportable: true,
      gridHeight: 700,
      exportFileName: `${dealerName}_${filterType}_${timeperiod}_${formattedDate}`.toLowerCase(),
      columnResizable: true,
      isRowSelectable: true,
      isOpportunity: true,
      multiSelectable: false,
      renderCellMapping: [
        { field: 'name', renderCell: RenderCellTypes.BUTTON },
        { field: 'first_name', renderCell: RenderCellTypes.BUTTON },
        { field: 'no_of_leads', renderCell: RenderCellTypes.COUNT_WITH_ICON },
        { field: 'LEAD_CONTACTED', renderCell: RenderCellTypes.COUNT_WITH_ICON },
        { field: 'LEAD_SOLD', renderCell: RenderCellTypes.COUNT_WITH_ICON },
        { field: 'lead_created_date_crm', renderCell: RenderCellTypes.DATE },
        { field: 'first_lead_creation_date', renderCell: RenderCellTypes.DATE },
        { field: 'latest_lead_active_date', renderCell: RenderCellTypes.DATE },
        { field: 'enrolled_date', renderCell: RenderCellTypes.DATE },
        { field: 'latest_contacted_date', renderCell: RenderCellTypes.DATE },
        { field: 'date_sold', renderCell: RenderCellTypes.DATE },
        { field: 'nurturing_status', renderCell: RenderCellTypes.STATUS },
        { field: 'conversion_rate_raw', renderCell: RenderCellTypes.RATE },
      ],
      filterableFields: [
        'name',
        'Altoona, PA',
        'Email',
        'Address1',
        'City',
        'State',
        'Zip',
        'Cell Phone',
        'Home Phone',
        'Work Phone',
        'DoNotEmail',
        'Status',
        'lead_type',
        'Source Description',
        'SalesRepName',
        'Wanted Veh Year',
        'Wanted Veh Make',
        'Wanted Veh Model',
        'Vehicle 1 Stock Number',
        'Vehicle 1 Vin',
        'New/Used',
        'Wanted Veh Trim',
        'Wanted Veh Price',
        'Date In',
        'Date Ir',
        'unique_crm_lead_id',
        'unique_crm_customer_id',
        'segment',
        'engagement_index',
        'total_messages_sent',
        'first_name',
        'Email_mailshake',
        'Enrolled Date',
        'Date Contacted',
        'Lead-to',
        'Sold Y/N',
        'Days in Nurturin',
        '# Nurturing M',
        'Enrolled Status',
        'Enrolled Source',
        'Enrolled VOI',
        'Sold Source',
        'Vehicle Bought',
        'Sales Person',
        'status',
        'no_of_leads',
        'LEAD_CONTACTED',
        'LEAD_SOLD',
        'conversion_rate_raw',
        'lead_created_date_crm',
        "enrolled_date",
        "date_sold",
        'lead_to_sale_days',
        'days_in_nurturing',
        'number_of_nurturing_messages',
        'vehicle_bought',
        'sold_vin',
        'sold_source',
        'sales_person',
        'enrolled_lead_status',
        'enrolled_source',
        'enrolled_voi',
        'latest_contacted_date',
        'latest_lead_active_date',
        'lead_to_contacted_days',
        'dealership_location',
        'sold_yn',
        'email',
        'first_lead_creation_date',
        'address',
      ],
    });
  }

  static OPPORTUNITIES_UNIQUE = (
    dealerId: string,
    mySqlFlag: Boolean,
    timeperiod: string | number,
    start_date: string,
    end_date: string,
  ) => {
    const leadFilter = JSON.stringify({});
    const queryParams =
      timeperiod === 'CUSTOM_RANGE'
        ? { dealer: dealerId, lead_filter: leadFilter, startDate: start_date, endDate: end_date }
        : { dealer: dealerId, lead_filter: leadFilter, timeperiod };

    return new DataGridTypes({
      apiDetails: { endpoint: `${DataGridTypes.API_VERSION_2}/leads`, queryParams },
      pageSize: 20,
      pageMode: PageMode.Scroll,
      filterable: true,
      renderMode: RenderMode.Server,
      editable: false,
      exportable: true,
      // gridHeight: 500,
      autoHeightBuffer: 30,
      exportFileName: `opportunities_unique_${timeperiod}`.toLowerCase(),
      columnResizable: true,
      isRowSelectable: true,
      isOpportunity: true,
      multiSelectable: false,
      renderCellMapping: [
        { field: 'name', renderCell: RenderCellTypes.BUTTON },
        { field: 'first_name', renderCell: RenderCellTypes.BUTTON },
        { field: 'no_of_leads', renderCell: RenderCellTypes.COUNT_WITH_ICON },
        { field: 'LEAD_CONTACTED', renderCell: RenderCellTypes.COUNT_WITH_ICON },
        { field: 'LEAD_SOLD', renderCell: RenderCellTypes.COUNT_WITH_ICON },
        { field: 'lead_created_date_crm', renderCell: RenderCellTypes.DATE },
        { field: 'first_lead_creation_date', renderCell: RenderCellTypes.DATE },
        { field: 'latest_lead_active_date', renderCell: RenderCellTypes.DATE },
        { field: 'enrolled_date', renderCell: RenderCellTypes.DATE },
        { field: 'latest_contacted_date', renderCell: RenderCellTypes.DATE },
        { field: 'date_sold', renderCell: RenderCellTypes.DATE },
        { field: 'nurturing_status', renderCell: RenderCellTypes.STATUS },
        { field: 'conversion_rate_raw', renderCell: RenderCellTypes.RATE },
      ],
      filterableFields: [
        'name',
        'Altoona, PA',
        'Email',
        'Address1',
        'City',
        'State',
        'Zip',
        'Cell Phone',
        'Home Phone',
        'Work Phone',
        'DoNotEmail',
        'Status',
        'lead_type',
        'Source Description',
        'SalesRepName',
        'Wanted Veh Year',
        'Wanted Veh Make',
        'Wanted Veh Model',
        'Vehicle 1 Stock Number',
        'Vehicle 1 Vin',
        'New/Used',
        'Wanted Veh Trim',
        'Wanted Veh Price',
        'Date In',
        'Date Ir',
        'unique_crm_lead_id',
        'unique_crm_customer_id',
        'segment',
        'engagement_index',
        'total_messages_sent',
        'first_name',
        'Email_mailshake',
        'Enrolled Date',
        'Date Contacted',
        'Lead-to',
        'Sold Y/N',
        'Days in Nurturin',
        '# Nurturing M',
        'Enrolled Status',
        'Enrolled Source',
        'Enrolled VOI',
        'Sold Source',
        'Vehicle Bought',
        'Sales Person',
        'status',
        'no_of_leads',
        'LEAD_CONTACTED',
        'LEAD_SOLD',
        'conversion_rate_raw',
        'lead_created_date_crm',
        "enrolled_date",
        "date_sold",
        'lead_to_sale_days',
        'days_in_nurturing',
        'number_of_nurturing_messages',
        'vehicle_bought',
        'sold_vin',
        'sold_source',
        'sales_person',
        'enrolled_lead_status',
        'enrolled_source',
        'enrolled_voi',
        'latest_contacted_date',
        'latest_lead_active_date',
        'lead_to_contacted_days',
        'dealership_location',
        'sold_yn',
        'email',
        'first_lead_creation_date',
        'address',
      ],
    });
  };

  static OPPORTUNITIES_UNDER_NURTURING = (
    dealerId: string,
    mySqlFlag: Boolean,
    timeperiod: string | number,
    start_date: string,
    end_date: string,
  ) => {
    const leadFilter = JSON.stringify({ nurturing_status: 'UNDER_NURTURING' });
    const queryParams =
      timeperiod === 'CUSTOM_RANGE'
        ? { dealer: dealerId, lead_filter: leadFilter, startDate: start_date, endDate: end_date }
        : { dealer: dealerId, lead_filter: leadFilter, timeperiod };

    return new DataGridTypes({
      apiDetails: { endpoint: `${DataGridTypes.API_VERSION_2}/leads`, queryParams },
      pageSize: 20,
      pageMode: PageMode.Scroll,
      filterable: true,
      renderMode: RenderMode.Server,
      editable: false,
      exportable: true,
      // gridHeight: 700,
      autoHeightBuffer: 30,
      exportFileName: `opportunities_under_nurturing_${timeperiod}`.toLowerCase(),
      columnResizable: true,
      isRowSelectable: true,
      isOpportunity: true,
      multiSelectable: false,
      renderCellMapping: [
        { field: 'name', renderCell: RenderCellTypes.BUTTON },
        { field: 'first_name', renderCell: RenderCellTypes.BUTTON },
      ],
    });
  };

  static OPPORTUNITIES_AVAILABLE_FOR_NURTURING = (
    dealerId: string,
    mySqlFlag: Boolean,
    timeperiod: string | number,
    start_date: string,
    end_date: string,
    multiSelectable = false,
    refreshKey?: number,
  ) => {
    const leadFilter = JSON.stringify({ nurturing_status: 'AVAILABLE' });
    const queryParams =
      timeperiod === 'CUSTOM_RANGE'
        ? {
            dealer: dealerId,
            lead_filter: leadFilter,
            startDate: start_date,
            endDate: end_date,
            ...(refreshKey != null ? { _refresh: refreshKey } : {}),
          }
        : {
            dealer: dealerId,
            lead_filter: leadFilter,
            timeperiod,
            ...(refreshKey != null ? { _refresh: refreshKey } : {}),
          };

    return new DataGridTypes({
      apiDetails: { endpoint: `${DataGridTypes.API_VERSION_2}/leads`, queryParams },
      pageSize: 20,
      pageMode: PageMode.Scroll,
      filterable: true,
      renderMode: RenderMode.Server,
      editable: false,
      exportable: true,
      // gridHeight: 660,
      autoHeightBuffer: 30,
      exportFileName: `opportunities_available_for_nurturing_${timeperiod}`.toLowerCase(),
      columnResizable: true,
      isRowSelectable: multiSelectable
        ? DataGridTypes.isAvailableRowSelectable
        : true,
      isOpportunity: true,
      multiSelectable,
      renderCellMapping: [
        { field: 'name', renderCell: RenderCellTypes.BUTTON },
        { field: 'first_name', renderCell: RenderCellTypes.BUTTON },
        { field: 'nurturing_status', renderCell: RenderCellTypes.STATUS },
      ],
      visibleColumns: [
        'lead_details_in_a_string',
        'name',
        'email',
        'segment',
        'engagement_index',
        'total_messages_sent',
        'latest_lead_active_date',
        'nurturing_status',
        'dealership_location',
        'middle_name',
        'address',
        'city',
        'state',
        'zip',
        'phone',
        'phone1',
        'phone2',
        'unsubscribes',
        'lead_status',
        'lead_type',
        'source',
        'sales_person',
        'voi_year',
        'voi_make',
        'voi_model',
        'voi_stock',
        'voi_vin',
        'voi_status',
        'voi_trim',
        'voi_price',
        'first_lead_creation_date',
        'unique_crm_lead_id',
        'unique_crm_customer_id',
      ],
      filterableFields: [
        'lead_details_in_a_string',
        'phone',
        'nurturing_status',
        'voi_year',
        'voi_make',
        'voi_model',
        'voi_stock',
        'voi_vin',
        'voi_status',
        'source',
      ],
    });
  };

  static UNSOLD = (dealerId: string, timeperiod: string = 'LAST_MONTH', mySqlFlag: boolean = true) => {
    const endpoint = `${DataGridTypes.API_VERSION_2}/leads/unsold`;

    // UNSOLD API supports LAST windows as: timeperiod=LAST&param=<N>.
    const lastNDaysMatch = /^LAST_(\d+|N)_DAYS$/i.exec(timeperiod);
    const normalizedTimeperiod = lastNDaysMatch ? 'LAST' : timeperiod;
    const lastNDaysParam = lastNDaysMatch?.[1]?.toUpperCase();

    const queryParams = {
      dealer_id: dealerId,
      timeperiod: normalizedTimeperiod,
      ...(lastNDaysParam ? { param: lastNDaysParam } : {}),
      mysql: mySqlFlag,
    };

    const now = new Date();
    const formattedDate = `${now.getFullYear()}${(now.getMonth() + 1)
      .toString().padStart(2, '0')}${now.getDate().toString().padStart(2, '0')}_${now.getHours()
        .toString().padStart(2, '0')}${now.getMinutes().toString().padStart(2, '0')}${now.getSeconds()
          .toString().padStart(2, '0')}`;

    return new DataGridTypes({
      apiDetails: { endpoint, queryParams },
      pageSize: 20,
      pageMode: PageMode.Scroll,
      filterable: true,
      renderMode: RenderMode.Server,
      editable: false,
      exportable: true,
      gridHeight: 700,
      exportFileName: `unsold_${timeperiod}_${formattedDate}`.toLowerCase(),
      columnResizable: true,
      isRowSelectable: true,
      isOpportunity: true,
      multiSelectable: false,
      renderCellMapping: [
        { field: 'name', renderCell: RenderCellTypes.BUTTON },
        { field: 'lead_created_date', renderCell: RenderCellTypes.DATE },
        { field: 'date_sold', renderCell: RenderCellTypes.DATE },
        { field: 'status', renderCell: RenderCellTypes.STATUS },
        { field: 'conversion_rate_raw', renderCell: RenderCellTypes.RATE },
      ],
      filterableFields: [
        'name',
        'email',
        'status',
        'source',
        'voi_name',
        'lead_intelligence',
        'suggested_strategy',
        'lead_created_date',
      ],
    });
  }

  static SCOPES = () =>
    new DataGridTypes({
      apiDetails: {
        endpoint: `${DataGridTypes.API_VERSION_2}/scopes`,
      },
      pageSize: 20,
      pageMode: PageMode.Scroll,
      filterable: true,
      renderMode: RenderMode.Server,
      editable: false,
      exportable: true,
      // gridHeight: 500,
      autoHeightBuffer: 40,
      exportFileName: 'scopes-export',
      columnResizable: true,
      isRowSelectable: true,
      isOpportunity: false,
      multiSelectable: false,
      renderCellMapping: [
        {
          field: 'name',
          renderCell: RenderCellTypes.BUTTON,
        },
        {
          field: 'status',
          renderCell: RenderCellTypes.STATUS,
        },
        {
          field: 'created_at',
          renderCell: RenderCellTypes.DATE,
        },
        {
          field: 'updated_at',
          renderCell: RenderCellTypes.DATE,
        },
      ],
      visibleColumns: ['name', 'description', 'scope', 'status', 'created_at', 'updated_at'],
      filterableFields: ['name', 'description', 'scope', 'status', 'created_at', 'updated_at'],
    });

  /** @param typeFilter - optional comma-separated types e.g. 'PAGE', 'COMPONENT', or 'PAGE,COMPONENT'. Omit for all. */
  static RESOURCES = (typeFilter?: string) =>
    new DataGridTypes({
      apiDetails: {
        endpoint: `${DataGridTypes.API_VERSION_2}/resources`,
        queryParams: typeFilter != null && typeFilter !== '' ? { type: typeFilter } : undefined,
      },
      pageSize: 20,
      pageMode: PageMode.Scroll,
      filterable: true,
      renderMode: RenderMode.Server,
      editable: false,
      exportable: true,
      // gridHeight: 500,
      autoHeightBuffer: 40,
      exportFileName: 'resources-export',
      columnResizable: true,
      isRowSelectable: true,
      isOpportunity: false,
      multiSelectable: false,
      renderCellMapping: [
        {
          field: 'name',
          renderCell: RenderCellTypes.RESOURCE_NAME,
        },
        {
          field: 'status',
          renderCell: RenderCellTypes.STATUS,
        },
        {
          field: 'created_at',
          renderCell: RenderCellTypes.DATE,
        },
        {
          field: 'updated_at',
          renderCell: RenderCellTypes.DATE,
        },
      ],
      visibleColumns: ['name', 'path', 'component', 'status', 'created_at', 'updated_at'],
      filterableFields: ['name', 'path', 'component', 'status', 'created_at', 'updated_at'],
    });

  static RUN_LOGS = (
    dealerId: string,
    quickFilters?: Record<string, string[]>,
    createdAtFilterModel?: { items: Array<{ id?: number; columnField: string; operatorValue: string; value?: any }>; linkOperator?: 'and' | 'or' },
  ) =>
    new DataGridTypes({
      apiDetails: {
        endpoint: `${DataGridTypes.API_VERSION_2}/run-v2`,
        queryParams: {
          dealer: dealerId,
          ...(quickFilters && Object.keys(quickFilters).length > 0
            ? Object.fromEntries(
                Object.entries(quickFilters)
                  .filter(([, values]) => Array.isArray(values) && values.length > 0)
                  .map(([key, values]) => [key, values.join(',')]),
              )
            : {}),
        },
      },
      initialFilterModel:
        createdAtFilterModel && createdAtFilterModel.items?.length > 0
          ? createdAtFilterModel
          : undefined,
      pageSize: 20,
      pageMode: PageMode.Scroll,
      filterable: true,
      renderMode: RenderMode.Server,
      editable: false,
      exportable: true,
      // gridHeight: 600,
      autoHeightBuffer: 40,
      exportFileName: 'run-logs-export',
      columnResizable: true,
      isRowSelectable: false,
      isOpportunity: false,
      multiSelectable: false,
      renderCellMapping: [
        { field: '_id', renderCell: RenderCellTypes.BUTTON_WITH_COPY },
        { field: 'name', renderCell: RenderCellTypes.TEXT },
        { field: 'run_type', renderCell: RenderCellTypes.TEXT },
        { field: 'run_by', renderCell: RenderCellTypes.TEXT },
        { field: 'run_by_name', renderCell: RenderCellTypes.TEXT },
        { field: 'created_at', renderCell: RenderCellTypes.DATETIME_UTC },
        { field: 'action_count', renderCell: RenderCellTypes.NUMBER },
        { field: 'status', renderCell: RenderCellTypes.RUN_LOG_STATUS_WITH_RETRY },
      ],
      columnSortModel: [
        { field: '_id', sort: 'desc' },
      ],
      visibleColumns: [
        '_id',
        'name',
        'run_type',
        "run_by",
        "run_by_name",
        'created_at',
        'action_count',
        'status',
      ],
      filterableFields: ['name', 'run_type', 'run_by', 'run_by_name', 'created_at', 'action_count', 'status'],
    });

  /** Optional filter: isActive, showExpired/showNonExpired, showPublished/showNonPublished. Sent via filters field (DataGrid filter model). */
  static LEASE_OFFERS = (
    dealerId?: string,
    offersFilter?: {
      isActive?: boolean;
      showExpired?: boolean;
      showNonExpired?: boolean;
      showPublished?: boolean;
      showNonPublished?: boolean;
    }
  ) => {
    const initialFilterModel = DataGridTypes.buildOffersFilterModel(offersFilter);
    return new DataGridTypes({
      apiDetails: {
        endpoint: `${DataGridTypes.API_VERSION_2}/offer`,
        queryParams: {
          offer_type: 'LEASE',
          ...(dealerId && dealerId !== 'default' && { dealer: dealerId }),
        },
      },
      initialFilterModel,
      pageSize: 20,
      pageMode: PageMode.Scroll,
      filterable: true,
      renderMode: RenderMode.Server,
      editable: false,
      exportable: true,
      gridHeight: 400,
      exportFileName: 'lease_offers_export',
      columnResizable: true,
      isRowSelectable: true,
      isOpportunity: true,
      multiSelectable: false,
      renderCellMapping: [
        { field: 'headline', renderCell: RenderCellTypes.OFFER_HEADLINE },
        { field: 'is_active', renderCell: RenderCellTypes.OFFER_ACTIVE_TOGGLE },
        { field: 'offer_image_url', renderCell: RenderCellTypes.IMAGE },
        { field: 'end_date', renderCell: RenderCellTypes.DATE },
        { field: 'actions', renderCell: RenderCellTypes.OFFER_ACTIONS },
      ],
      visibleColumns: ['headline', 'vehicle_model', 'is_active', 'offer_image_url', 'end_date', 'actions'],
      filterableFields: ['headline', 'vehicle_model', 'offer_image_url', 'end_date'],
    });
  };

  /** Build DataGrid filter model for offers: is_active, end_date (expired/non-expired), status (published/non-published). Used in filters query param. */
  static buildOffersFilterModel(offersFilter?: {
    isActive?: boolean;
    showExpired?: boolean;
    showNonExpired?: boolean;
    showPublished?: boolean;
    showNonPublished?: boolean;
  }): { items: Array<{ id?: number; columnField: string; operatorValue: string; value?: any }>; linkOperator?: 'and' | 'or' } {
    const items: Array<{ id?: number; columnField: string; operatorValue: string; value?: any }> = [];
    let id = 1;
    if (offersFilter?.isActive === true) {
      items.push({ id: id++, columnField: 'is_active', operatorValue: 'equals', value: true });
    }
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const todayISO = today.toISOString();
    const onlyNonExpired = offersFilter?.showNonExpired === true && offersFilter?.showExpired !== true;
    const onlyExpired = offersFilter?.showExpired === true && offersFilter?.showNonExpired !== true;
    if (onlyNonExpired) {
      items.push({ id: id++, columnField: 'end_date', operatorValue: 'after', value: todayISO });
    } else if (onlyExpired) {
      items.push({ id: id++, columnField: 'end_date', operatorValue: 'before', value: todayISO });
    }
    const onlyPublished = offersFilter?.showPublished === true && offersFilter?.showNonPublished !== true;
    const onlyNonPublished = offersFilter?.showNonPublished === true && offersFilter?.showPublished !== true;
    if (onlyPublished) {
      items.push({ id: id++, columnField: 'status', operatorValue: 'equals', value: 'PUBLISHED' });
    } else if (onlyNonPublished) {
      items.push({ id: id++, columnField: 'status', operatorValue: '!=', value: 'PUBLISHED' });
    }
    return { items, linkOperator: 'and' };
  }

  static LEASE_FINANCE_OFFERS = (
    dealerId?: string,
    offersFilter?: {
      isActive?: boolean;
      showExpired?: boolean;
      showNonExpired?: boolean;
      showPublished?: boolean;
      showNonPublished?: boolean;
    }
  ) => {
    const initialFilterModel = DataGridTypes.buildOffersFilterModel(offersFilter);
    return new DataGridTypes({
      apiDetails: {
        endpoint: `${DataGridTypes.API_VERSION_2}/offer`,
        queryParams: {
          offer_type: 'LEASE_FINANCE',
          ...(dealerId && dealerId !== 'default' && { dealer: dealerId }),
        },
      },
      initialFilterModel,
      pageSize: 20,
      pageMode: PageMode.Scroll,
      filterable: true,
      renderMode: RenderMode.Server,
      editable: false,
      exportable: true,
      gridHeight: 400,
      exportFileName: 'lease_finance_offers_export',
      columnResizable: true,
      isRowSelectable: true,
      isOpportunity: true,
      multiSelectable: false,
      renderCellMapping: [
        { field: 'headline', renderCell: RenderCellTypes.OFFER_HEADLINE },
        { field: 'is_active', renderCell: RenderCellTypes.OFFER_ACTIVE_TOGGLE },
        { field: 'offer_image_url', renderCell: RenderCellTypes.IMAGE },
        { field: 'end_date', renderCell: RenderCellTypes.DATE },
        { field: 'actions', renderCell: RenderCellTypes.OFFER_ACTIONS },
      ],
      visibleColumns: ['headline', 'vehicle_model', 'is_active', 'offer_image_url', 'end_date', 'actions'],
      filterableFields: ['headline', 'vehicle_model', 'offer_image_url', 'end_date'],
    });
  };

  static FINANCE_OFFERS = (
    dealerId?: string,
    offersFilter?: {
      isActive?: boolean;
      showExpired?: boolean;
      showNonExpired?: boolean;
      showPublished?: boolean;
      showNonPublished?: boolean;
    }
  ) => {
    const initialFilterModel = DataGridTypes.buildOffersFilterModel(offersFilter);
    return new DataGridTypes({
      apiDetails: {
        endpoint: `${DataGridTypes.API_VERSION_2}/offer`,
        queryParams: {
          offer_type: 'FINANCE',
          ...(dealerId && dealerId !== 'default' && { dealer: dealerId }),
        },
      },
      initialFilterModel,
      pageSize: 20,
      pageMode: PageMode.Scroll,
      filterable: true,
      renderMode: RenderMode.Server,
      editable: false,
      exportable: true,
      gridHeight: 400,
      exportFileName: 'finance_offers_export',
      columnResizable: true,
      isRowSelectable: true,
      isOpportunity: true,
      multiSelectable: false,
      renderCellMapping: [
        { field: 'headline', renderCell: RenderCellTypes.OFFER_HEADLINE },
        { field: 'is_active', renderCell: RenderCellTypes.OFFER_ACTIVE_TOGGLE },
        { field: 'offer_image_url', renderCell: RenderCellTypes.IMAGE },
        { field: 'end_date', renderCell: RenderCellTypes.DATE },
        { field: 'actions', renderCell: RenderCellTypes.OFFER_ACTIONS },
      ],
      visibleColumns: ['headline', 'vehicle_model', 'is_active', 'offer_image_url', 'end_date', 'actions'],
      filterableFields: ['headline', 'vehicle_model', 'offer_image_url', 'end_date'],
    });
  };

  static VALUE_CONTENT = (dealerId?: string) =>
    new DataGridTypes({
      apiDetails: {
        endpoint: `${DataGridTypes.API_VERSION_2}/dealer-value`,
        queryParams: {
          ...(dealerId && dealerId !== 'default' && { dealer: dealerId })
        },
      },
      pageSize: 20,
      pageMode: PageMode.Scroll,
      filterable: true,
      renderMode: RenderMode.Server,
      editable: false,
      exportable: true,
      // gridHeight: 500,
      autoHeightBuffer: 74,
      exportFileName: 'value_content_export',
      columnResizable: true,
      isRowSelectable: true,
      isOpportunity: false,
      multiSelectable: false,
      renderCellMapping: [
        { field: 'headline', renderCell: RenderCellTypes.BUTTON },
        { field: 'value_type', renderCell: RenderCellTypes.STATUS },
        { field: 'end_date', renderCell: RenderCellTypes.DATE },
        { field: 'actions', renderCell: RenderCellTypes.ACTION_BUTTONS },
      ],
      visibleColumns: ['headline', 'value_type', 'end_date', 'actions'],
      filterableFields: ['headline', 'value_type', 'end_date'],
    });

  static FOOTER_IMAGE = (dealerId?: string) =>
    new DataGridTypes({
      apiDetails: {
        endpoint: `${DataGridTypes.API_VERSION_2}/footer-image`,
        queryParams: {
          ...(dealerId && dealerId !== 'default' && { dealer: dealerId })
        },
      },
      pageSize: 20,
      pageMode: PageMode.Scroll,
      filterable: true,
      renderMode: RenderMode.Server,
      editable: false,
      exportable: true,
      // gridHeight: 500,
      autoHeightBuffer: 74,
      exportFileName: 'footer_image_export',
      columnResizable: true,
      isRowSelectable: false,
      isOpportunity: false,
      multiSelectable: false,
      renderCellMapping: [
        { field: 'image_description', renderCell: RenderCellTypes.BUTTON },
        { field: 'image_address', renderCell: RenderCellTypes.TEXT },
        { field: 'image_url', renderCell: RenderCellTypes.TEXT },
        { field: 'expiry_date', renderCell: RenderCellTypes.DATE },
        { field: 'actions', renderCell: RenderCellTypes.ACTION_BUTTONS },
      ],
      visibleColumns: ['image_description', 'image_url', 'expiry_date', 'actions'],
    });

  static DEALER_SPECIALS = (dealerId?: string) =>
    new DataGridTypes({
      apiDetails: {
        endpoint: `${DataGridTypes.API_VERSION_2}/dealer-special`,
        queryParams: {
          ...(dealerId && dealerId !== 'default' && { dealer: dealerId })
        },
      },
      pageSize: 20,
      pageMode: PageMode.Scroll,
      filterable: true,
      renderMode: RenderMode.Server,
      editable: false,
      exportable: true,
      // gridHeight: 500,
      autoHeightBuffer: 74,
      exportFileName: 'dealer_specials_export',
      columnResizable: true,
      isRowSelectable: false,
      isOpportunity: false,
      multiSelectable: false,
      renderCellMapping: [
        { field: 'special_headline', renderCell: RenderCellTypes.BUTTON },
        { field: 'special_description', renderCell: RenderCellTypes.TEXT },
        { field: 'start_date', renderCell: RenderCellTypes.DATE },
        { field: 'end_date', renderCell: RenderCellTypes.DATE },
        { field: 'applicable_model', renderCell: RenderCellTypes.CHIP_ARRAY },
        { field: 'disclaimer_text', renderCell: RenderCellTypes.TEXT },
        { field: 'actions', renderCell: RenderCellTypes.ACTION_BUTTONS },
      ],
      visibleColumns: ['special_headline', 'end_date', 'applicable_model', 'actions'],
      filterableFields: ['special_headline', 'end_date', 'applicable_model'],
    });

  /** statusFilter: { showActive, showArchived }. metadataFilters: optional Record<key, string[]>; keys 'name'|'type'|'generated_type'|'subject' become direct query params, other keys become metadata.<key>. */
  static EMAIL_TEMPLATES = (
    dealerId: string,
    siteTitle?: string,
    statusFilter?: { showActive: boolean; showArchived: boolean },
    metadataFilters?: Record<string, string[]>,
    templateIds?: string[],
  ) => {
    const TEMPLATE_FIELD_KEYS = ['name', 'type', 'generated_type', 'subject', 'status'];
    const now = new Date();
    const dateStr = `${now.getFullYear()}-${(now.getMonth() + 1).toString().padStart(2, '0')}-${now.getDate().toString().padStart(2, '0')}`;
    const baseName = siteTitle
      ? `${siteTitle.split(' ').join('_')}_all_email_templates`
      : 'all_email_templates';
    const showActive = statusFilter?.showActive !== false;
    const showArchived = statusFilter?.showArchived !== false;
    const statusParam =
      showActive && showArchived
        ? undefined
        : [showActive && 'ACTIVE', showArchived && 'ARCHIVED'].filter(Boolean).join(',');
    const filterParams =
      metadataFilters && Object.keys(metadataFilters).length > 0
        ? Object.fromEntries(
            Object.entries(metadataFilters)
              .filter(([, v]) => Array.isArray(v) && v.length > 0)
              .map(([k, v]) =>
                TEMPLATE_FIELD_KEYS.includes(k)
                  ? [k, (v as string[]).join(',')]
                  : [`metadata.${k}`, (v as string[]).join(',')],
              ),
          )
        : {};
    return new DataGridTypes({
      apiDetails: {
        endpoint: `${DataGridTypes.API_VERSION_2}/templates`,
        queryParams: {
          dealer: dealerId,
          ...(statusParam && { status: statusParam }),
          ...filterParams,
          ...(templateIds?.length
            ? { template_ids: templateIds.join(',') }
            : {}),
        },
      },
      pageSize: 20,
      pageMode: PageMode.Scroll,
      filterable: true,
      renderMode: RenderMode.Server,
      editable: false,
      exportable: true,
      // gridHeight: 700,
      autoHeightBuffer: 40,
      exportFileName: `${baseName}_${dateStr}`,
      columnResizable: true,
      isRowSelectable: true,
      isOpportunity: false,
      multiSelectable: true,
      renderCellMapping: [
        { field: 'name', renderCell: RenderCellTypes.BUTTON },
        { field: 'type', renderCell: RenderCellTypes.TEXT },
        { field: 'subject', renderCell: RenderCellTypes.TEXT },
        { field: 'status', renderCell: RenderCellTypes.EMAIL_TEMPLATE_STATUS_DROPDOWN },
        // { field: 'workflow', renderCell: RenderCellTypes.TEXT },
        { field: 'created_by', renderCell: RenderCellTypes.TEXT },
        { field: 'expiration_date', renderCell: RenderCellTypes.DATE },
        { field: 'metadata', renderCell: RenderCellTypes.METADATA_MODAL },
        { field: 'action', renderCell: RenderCellTypes.EMAIL_TEMPLATE_ACTIONS },
      ],
      columnSortModel: [{ field: 'created_at', sort: 'desc' as const }],
      visibleColumns: ['name', 'type', 'subject', 'status', 'created_by', 'expiration_date', 'metadata', 'action'],
      filterableFields: ['name', 'type', 'subject', 'status', 'created_by'],
    });
  };

  /**
   * Test Nurturing → Nurturing Journeys
   * Uses automation opportunities endpoint:
   * GET /api/v2/nurturing-automation/opportunities?dealer_id=:id&opportunity_type=test
   *
   * @param metadataFilters Optional Quick Filter selections keyed by field name
   * (`segment` | `nurturing_stage_label` | `test_date` | `last_retry_date` etc.).
   * Each entry is joined
   * with commas and forwarded as a top-level query param (no `metadata.` prefix —
   * the opportunities API filters on row fields directly).
   */
  static TEST_NURTURING_JOURNEYS_LEADS = (
    dealerId: string,
    opportunityType: string = 'test',
    metadataFilters?: Record<string, string[]>,
  ) => {
    const filterParams =
      metadataFilters && Object.keys(metadataFilters).length > 0
        ? Object.fromEntries(
            Object.entries(metadataFilters)
              .filter(([, v]) => Array.isArray(v) && v.length > 0)
              .map(([k, v]) => [k, (v as string[]).join(',')]),
          )
        : {};
    return new DataGridTypes({
      apiDetails: {
        endpoint: `${DataGridTypes.API_VERSION_2}/nurturing-automation/opportunities`,
        queryParams: {
          dealer_id: dealerId,
          opportunity_type: opportunityType,
          ...filterParams,
        },
      },
      pageSize: 20,
      pageMode: PageMode.Scroll,
      filterable: true,
      renderMode: RenderMode.Server,
      editable: false,
      exportable: true,
      autoHeightBuffer: 30,
      exportFileName: `test_nurturing_journeys_${dealerId}`,
      columnResizable: true,
      isRowSelectable: false,
      isOpportunity: true,
      multiSelectable: false,
      renderCellMapping: [
        {
          field: 'nurturing_automation_status',
          renderCell: RenderCellTypes.TEST_NURTURING_AUTOMATION_STATUS,
        },
        { field: 'name', renderCell: RenderCellTypes.BUTTON },
        { field: 'segment', renderCell: RenderCellTypes.TEXT },
        { field: 'nurturing_stage_label', renderCell: RenderCellTypes.TEXT },
        { field: 'test_date', renderCell: RenderCellTypes.DATETIME_DEALER },
        { field: 'last_retry_date', renderCell: RenderCellTypes.DATETIME_DEALER },
        { field: 'expected_sent_count', renderCell: RenderCellTypes.NUMBER },
        { field: 'messages_sent_count', renderCell: RenderCellTypes.NUMBER },
      ],
      visibleColumns: [
        'nurturing_automation_status',
        'name',
        'segment',
        'nurturing_stage_label',
        'test_date',
        'last_retry_date',
        'expected_sent_count',
        'messages_sent_count',
      ],
      filterableFields: ['name', 'segment', 'nurturing_stage_label'],
      columnDefOverrides: {
        nurturing_automation_status: { width: 128, minWidth: 108, maxWidth: 152 },
        name: { width: 210, minWidth: 190 },
        test_date: { width: 210, minWidth: 190 },
        last_retry_date: { width: 210, minWidth: 190 },
        expected_sent_count: { width: 170, minWidth: 150 },
        messages_sent_count: { width: 170, minWidth: 150 },
      },
    });
  };

  /**
   * Test Nurturing → Generated Templates: same API as EMAIL_TEMPLATES(); narrow columns;
   * `segment` is synthetic and reads metadata segment name (see EMAIL_TEMPLATE_SEGMENT_NAME).
   */
  static TEST_GENERATED_TEMPLATES = (
    dealerId: string,
    siteTitle?: string,
    statusFilter?: { showActive: boolean; showArchived: boolean },
    metadataFilters?: Record<string, string[]>,
  ) => {
    const grid = DataGridTypes.EMAIL_TEMPLATES(
      dealerId,
      siteTitle,
      statusFilter,
      metadataFilters,
    );
    grid.visibleColumns = ['name', 'segment', 'subject', 'status'];
    grid.filterableFields = ['name', 'subject', 'status'];
    grid.columnSortModel = [{ field: 'updated_at', sort: 'desc' as const }];
    grid.exportFileName = `test_generated_templates_${dealerId}`;
    const existing = grid.renderCellMapping || [];
    const withoutStatus = existing.filter(m => m.field !== 'status');
    const nextMapping = [
      ...withoutStatus,
      { field: 'status', renderCell: RenderCellTypes.STATUS },
    ];
    if (!nextMapping.some(m => m.field === 'segment')) {
      nextMapping.push({
        field: 'segment',
        renderCell: RenderCellTypes.EMAIL_TEMPLATE_SEGMENT_NAME,
      });
    }
    grid.renderCellMapping = nextMapping;
    grid.columnDefOverrides = {
      name: { flex: 2, minWidth: 240 },
      subject: { flex: 2, minWidth: 260 },
      segment: { flex: 0, width: 88, minWidth: 72, maxWidth: 112 },
      status: { flex: 0, width: 104, minWidth: 96, maxWidth: 128 },
    };
    return grid;
  };

  /**
   * Configure → Static Content - Value Messages:
   * - Same templates API as EMAIL_TEMPLATES (caller filters `generated_type` = static).
   * - Read-only status chip; default sort by expiration_date.
   */
  static STATIC_CONTENT_VALUE_MESSAGES_TEMPLATES = (
    dealerId: string,
    siteTitle?: string,
    statusFilter?: { showActive: boolean; showArchived: boolean },
    metadataFilters?: Record<string, string[]>,
  ) => {
    const grid = DataGridTypes.EMAIL_TEMPLATES(
      dealerId,
      siteTitle,
      statusFilter,
      metadataFilters,
    );
    grid.columnSortModel = [{ field: 'expiration_date', sort: 'desc' as const }];
    grid.renderCellMapping = (grid.renderCellMapping || []).map((mapping) =>
      mapping.field === 'status'
        ? { ...mapping, renderCell: RenderCellTypes.STATUS }
        : mapping,
    );
    return grid;
  };

  /**
   * Human-in-the-loop pending approval grid:
   * - Same templates API.
   * - Narrow columns and custom actions (edit / approve / preview).
   * - Segment derived from metadata segment name.
   */
  static HIL_PENDING_APPROVAL_TEMPLATES = (
    dealerId: string,
    siteTitle?: string,
    statusFilter?: { showActive: boolean; showArchived: boolean },
    metadataFilters?: Record<string, string[]>,
    templateIds?: string[],
  ) => {
    const pendingApprovalFilters: Record<string, string[]> = {
      status: ['PENDING_APPROVAL'],
      ...metadataFilters,
    };
    const grid = DataGridTypes.EMAIL_TEMPLATES(
      dealerId,
      siteTitle,
      statusFilter,
      pendingApprovalFilters,
      templateIds,
    );
    grid.visibleColumns = ['name', 'segment', 'type', 'subject', 'created_at', 'action'];
    grid.filterableFields = ['name', 'segment', 'type', 'subject', 'status'];
    grid.columnSortModel = [{ field: 'created_at', sort: 'desc' as const }];
    grid.exportFileName = `hil_pending_templates_${dealerId}`;
    grid.renderCellMapping = [
      { field: 'name', renderCell: RenderCellTypes.TEXT },
      { field: 'type', renderCell: RenderCellTypes.HIL_TEMPLATE_GENERATED_TYPE },
      { field: 'subject', renderCell: RenderCellTypes.TEXT },
      { field: 'created_at', renderCell: RenderCellTypes.DATE },
      { field: 'segment', renderCell: RenderCellTypes.EMAIL_TEMPLATE_SEGMENT_NAME },
      { field: 'action', renderCell: RenderCellTypes.HIL_TEMPLATE_ACTIONS },
    ];
    grid.columnDefOverrides = {
      name: { flex: 1.6, minWidth: 220 },
      segment: { flex: 1.2, minWidth: 180 },
      type: { flex: 0.8, minWidth: 120, maxWidth: 160 },
      subject: { flex: 2, minWidth: 260 },
      created_at: { flex: 0.9, minWidth: 150, maxWidth: 180 },
      action: { flex: 1, minWidth: 200, maxWidth: 240 },
    };
    grid.autoHeightBuffer = 12;
    return grid;
  };

  /**
   * Human-in-the-loop segments needing attention:
   * - Uses automation-needing-attention API.
   * - Shows segment issue summary rows raised by automation.
   */
  static HIL_SEGMENTS_NEEDING_ATTENTION = (
    dealerId: string,
    opportunityType: string = 'test',
  ) =>
    new DataGridTypes({
      apiDetails: {
        endpoint: `${DataGridTypes.API_VERSION_2}/nurturing-automation/automation-needing-attention`,
        queryParams: {
          dealer_id: dealerId,
          opportunity_type: opportunityType,
        },
      },
      pageSize: 20,
      pageMode: PageMode.Scroll,
      filterable: true,
      renderMode: RenderMode.Server,
      editable: false,
      exportable: true,
      autoHeightBuffer: 30,
      exportFileName: `hil_segments_needing_attention_${dealerId}`,
      columnResizable: true,
      isRowSelectable: false,
      isOpportunity: true,
      multiSelectable: false,
      renderCellMapping: [
        { field: 'segment_name', renderCell: RenderCellTypes.TEXT },
        { field: 'issue', renderCell: RenderCellTypes.TEXT },
        { field: 'impacted_count', renderCell: RenderCellTypes.NUMBER },
        { field: 'date_raised', renderCell: RenderCellTypes.DATE },
        {
          field: 'metadata',
          renderCell: RenderCellTypes.METADATA_MODAL,
          cellProps: {
            headerChipFields: ['segment_name', 'reason_code', 'issue'],
          },
        },
        { field: 'actions', renderCell: RenderCellTypes.HIL_SEGMENTS_ATTENTION_ACTIONS },
      ],
      visibleColumns: [
        'segment_name',
        'issue',
        'impacted_count',
        'date_raised',
        'metadata',
        'actions',
      ],
      filterableFields: ['segment_name', 'issue'],
      columnSortModel: [{ field: 'date_raised', sort: 'desc' as const }],
      columnDefOverrides: {
        actions: { flex: 0.7, minWidth: 108, maxWidth: 132 },
      },
    });
}

export default DataGridTypes;
